<script setup>
import { ref, computed, inject } from 'vue';
import SubHead from '@/components/result/SubHead.vue';
import ExamStatus from '@/components/result/ExamStatus.vue';
import ComparisonChart from '@/components/result/ComparisonChart.vue'; 
import AnswerRates from '@/components/result/AnswerRates.vue';

import numeracyGraph02 from '@/assets/images/result/numeracy_graph_02.png';
import literacyGraph02 from '@/assets/images/result/literacy_graph_02.png';

// API 데이터에서 해당 학년 데이터 찾기
const currentGradeData = computed(() => {
  if (!schoolData.value?.totalList) return null;
  return schoolData.value.totalList.find(item => item.grade_code === grade.value);
});

// API 데이터에서 해당 학년의 반 데이터들 찾기
const currentGradeClasses = computed(() => {
  if (!schoolData.value?.classList) return [];
  return schoolData.value.classList.filter(item => item.grade_code === grade.value);
});

// 검사 현황 데이터
const examData = computed(() => {
  if (!currentGradeData.value || !currentGradeClasses.value.length) {
    return [
      { label: '전체학교', schoolCount: '0', classCount: '0', studentCount: '0', male: '0(0%)', female: '0(0%)' },
      { label: '우리학교', schoolCount: '', classCount: '0', studentCount: '0', male: '0(0%)', female: '0(0%)' }
    ];
  }

  const gradeData = currentGradeData.value;
  const prefix = props.subject === 'literacy' ? 'kor' : 'mat';
  
  // 전체 학교 과목별 응시 학생 수 계산
  const totalMaleStudents = props.subject === 'literacy' ? (gradeData.male_cnt || 0) : (gradeData.mat_male_cnt || 0);
  const totalFemaleStudents = props.subject === 'literacy' ? (gradeData.female_cnt || 0) : (gradeData.mat_female_cnt || 0);
  
  const totalStudents = totalMaleStudents + totalFemaleStudents;
  const malePercent = totalStudents > 0 ? ((totalMaleStudents / totalStudents) * 100).toFixed(1) : 0;
  const femalePercent = totalStudents > 0 ? ((totalFemaleStudents / totalStudents) * 100).toFixed(1) : 0;

  // 우리 학교 과목별 응시 학생 수 계산
  const ourMaleCount = props.subject === 'literacy' ? 
    currentGradeClasses.value.reduce((sum, cls) => sum + (cls.male_count || 0), 0) :
    currentGradeClasses.value.reduce((sum, cls) => sum + (cls.mat_male_count || 0), 0);
  
  const ourFemaleCount = props.subject === 'literacy' ? 
    currentGradeClasses.value.reduce((sum, cls) => sum + (cls.female_count || 0), 0) :
    currentGradeClasses.value.reduce((sum, cls) => sum + (cls.mat_female_count || 0), 0);
  
  const ourTotalStudents = ourMaleCount + ourFemaleCount;
  const ourMalePercent = ourTotalStudents > 0 ? ((ourMaleCount / ourTotalStudents) * 100).toFixed(1) : 0;
  const ourFemalePercent = ourTotalStudents > 0 ? ((ourFemaleCount / ourTotalStudents) * 100).toFixed(1) : 0;

  return [
    { 
      label: '전체학교', 
      schoolCount: gradeData.school_cnt?.toString() || '0', 
      classCount: gradeData.class_cnt?.toString() || '0', 
      studentCount: totalStudents?.toString() || '0', 
      male: `${totalMaleStudents || 0}(${malePercent}%)`, 
      female: `${totalFemaleStudents || 0}(${femalePercent}%)` 
    },
    { 
      label: '우리학교', 
      schoolCount: '', 
      classCount: currentGradeClasses.value.length.toString(), 
      studentCount: ourTotalStudents.toString(), 
      male: `${ourMaleCount}(${ourMalePercent}%)`, 
      female: `${ourFemaleCount}(${ourFemalePercent}%)` 
    }
  ];
});

// 수준별 비교 데이터
const ComparisonData = computed(() => {
  if (!currentGradeData.value || !currentGradeClasses.value.length) {
    return [
      { label: '1수준', totalSchool: 0, totalSchoolMale: 0, totalSchoolFemale: 0, ourSchool: 0, ourSchoolCount: '0', ourSchoolMale: 0, ourSchoolMaleCount: '0', ourSchoolFemale: 0, ourSchoolFemaleCount: '0' },
      { label: '2수준', totalSchool: 0, totalSchoolMale: 0, totalSchoolFemale: 0, ourSchool: 0, ourSchoolCount: '0', ourSchoolMale: 0, ourSchoolMaleCount: '0', ourSchoolFemale: 0, ourSchoolFemaleCount: '0' },
      { label: '3수준', totalSchool: 0, totalSchoolMale: 0, totalSchoolFemale: 0, ourSchool: 0, ourSchoolCount: '0', ourSchoolMale: 0, ourSchoolMaleCount: '0', ourSchoolFemale: 0, ourSchoolFemaleCount: '0' },
      { label: '4수준', totalSchool: 0, totalSchoolMale: 0, totalSchoolFemale: 0, ourSchool: 0, ourSchoolCount: '0', ourSchoolMale: 0, ourSchoolMaleCount: '0', ourSchoolFemale: 0, ourSchoolFemaleCount: '0' }
    ];
  }

  const gradeData = currentGradeData.value;
  const prefix = props.subject === 'literacy' ? 'kor' : 'mat';
  
  // 전체 학교 남학생 데이터 (korlevel은 이미 남학생 데이터)
  const totalMaleLevel1 = gradeData[`${prefix}level1`];
  const totalMaleLevel2 = gradeData[`${prefix}level2`];
  const totalMaleLevel3 = gradeData[`${prefix}level3`];
  const totalMaleLevel4 = gradeData[`${prefix}level4`];
  const totalMaleAll = totalMaleLevel1 + totalMaleLevel2 + totalMaleLevel3 + totalMaleLevel4;
  
  // 전체 학교 여학생 데이터
  const totalFemaleLevel1 = gradeData[`female_${prefix}level1`];
  const totalFemaleLevel2 = gradeData[`female_${prefix}level2`];
  const totalFemaleLevel3 = gradeData[`female_${prefix}level3`];
  const totalFemaleLevel4 = gradeData[`female_${prefix}level4`];
  const totalFemaleAll = totalFemaleLevel1 + totalFemaleLevel2 + totalFemaleLevel3 + totalFemaleLevel4;
  
  // 전체 학교 전체 데이터 (남학생 + 여학생)
  const totalLevel1 = totalMaleLevel1 + totalFemaleLevel1;
  const totalLevel2 = totalMaleLevel2 + totalFemaleLevel2;
  const totalLevel3 = totalMaleLevel3 + totalFemaleLevel3;
  const totalLevel4 = totalMaleLevel4 + totalFemaleLevel4;
  const totalAll = totalLevel1 + totalLevel2 + totalLevel3 + totalLevel4;
  
  // 우리 학교 남학생 데이터
  const ourMaleLevel1 = currentGradeClasses.value.reduce((sum, cls) => sum + cls[`${prefix}level1`], 0);
  const ourMaleLevel2 = currentGradeClasses.value.reduce((sum, cls) => sum + cls[`${prefix}level2`], 0);
  const ourMaleLevel3 = currentGradeClasses.value.reduce((sum, cls) => sum + cls[`${prefix}level3`], 0);
  const ourMaleLevel4 = currentGradeClasses.value.reduce((sum, cls) => sum + cls[`${prefix}level4`], 0);
  const ourMaleAll = ourMaleLevel1 + ourMaleLevel2 + ourMaleLevel3 + ourMaleLevel4;
  
  // 우리 학교 여학생 데이터
  const ourFemaleLevel1 = currentGradeClasses.value.reduce((sum, cls) => sum + cls[`female_${prefix}level1`], 0);
  const ourFemaleLevel2 = currentGradeClasses.value.reduce((sum, cls) => sum + cls[`female_${prefix}level2`], 0);
  const ourFemaleLevel3 = currentGradeClasses.value.reduce((sum, cls) => sum + cls[`female_${prefix}level3`], 0);
  const ourFemaleLevel4 = currentGradeClasses.value.reduce((sum, cls) => sum + cls[`female_${prefix}level4`], 0);
  const ourFemaleAll = ourFemaleLevel1 + ourFemaleLevel2 + ourFemaleLevel3 + ourFemaleLevel4;
  
  // 우리 학교 전체 데이터 (남학생 + 여학생)
  const ourLevel1 = ourMaleLevel1 + ourFemaleLevel1;
  const ourLevel2 = ourMaleLevel2 + ourFemaleLevel2;
  const ourLevel3 = ourMaleLevel3 + ourFemaleLevel3;
  const ourLevel4 = ourMaleLevel4 + ourFemaleLevel4;
  const ourTotalAll = ourLevel1 + ourLevel2 + ourLevel3 + ourLevel4;

  return [
    {
      label: '1수준',
      // 좌측 그래프 (전체)
      totalSchool: totalAll > 0 ? parseFloat(((totalLevel1 / totalAll) * 100).toFixed(1)) : 0,
      ourSchool: ourTotalAll > 0 ? parseFloat(((ourLevel1 / ourTotalAll) * 100).toFixed(1)) : 0,
      ourSchoolCount: ourLevel1.toString(),
      // 우측 그래프 (성별)
      totalSchoolMale: totalMaleAll > 0 ? parseFloat(((totalMaleLevel1 / totalMaleAll) * 100).toFixed(1)) : 0,
      totalSchoolFemale: totalFemaleAll > 0 ? parseFloat(((totalFemaleLevel1 / totalFemaleAll) * 100).toFixed(1)) : 0,
      ourSchoolMale: ourMaleAll > 0 ? parseFloat(((ourMaleLevel1 / ourMaleAll) * 100).toFixed(1)) : 0,
      ourSchoolMaleCount: ourMaleLevel1.toString(),
      ourSchoolFemale: ourFemaleAll > 0 ? parseFloat(((ourFemaleLevel1 / ourFemaleAll) * 100).toFixed(1)) : 0,
      ourSchoolFemaleCount: ourFemaleLevel1.toString()
    },
    {
      label: '2수준',
      totalSchool: totalAll > 0 ? parseFloat(((totalLevel2 / totalAll) * 100).toFixed(1)) : 0,
      ourSchool: ourTotalAll > 0 ? parseFloat(((ourLevel2 / ourTotalAll) * 100).toFixed(1)) : 0,
      ourSchoolCount: ourLevel2.toString(),
      totalSchoolMale: totalMaleAll > 0 ? parseFloat(((totalMaleLevel2 / totalMaleAll) * 100).toFixed(1)) : 0,
      totalSchoolFemale: totalFemaleAll > 0 ? parseFloat(((totalFemaleLevel2 / totalFemaleAll) * 100).toFixed(1)) : 0,
      ourSchoolMale: ourMaleAll > 0 ? parseFloat(((ourMaleLevel2 / ourMaleAll) * 100).toFixed(1)) : 0,
      ourSchoolMaleCount: ourMaleLevel2.toString(),
      ourSchoolFemale: ourFemaleAll > 0 ? parseFloat(((ourFemaleLevel2 / ourFemaleAll) * 100).toFixed(1)) : 0,
      ourSchoolFemaleCount: ourFemaleLevel2.toString()
    },
    {
      label: '3수준',
      totalSchool: totalAll > 0 ? parseFloat(((totalLevel3 / totalAll) * 100).toFixed(1)) : 0,
      ourSchool: ourTotalAll > 0 ? parseFloat(((ourLevel3 / ourTotalAll) * 100).toFixed(1)) : 0,
      ourSchoolCount: ourLevel3.toString(),
      totalSchoolMale: totalMaleAll > 0 ? parseFloat(((totalMaleLevel3 / totalMaleAll) * 100).toFixed(1)) : 0,
      totalSchoolFemale: totalFemaleAll > 0 ? parseFloat(((totalFemaleLevel3 / totalFemaleAll) * 100).toFixed(1)) : 0,
      ourSchoolMale: ourMaleAll > 0 ? parseFloat(((ourMaleLevel3 / ourMaleAll) * 100).toFixed(1)) : 0,
      ourSchoolMaleCount: ourMaleLevel3.toString(),
      ourSchoolFemale: ourFemaleAll > 0 ? parseFloat(((ourFemaleLevel3 / ourFemaleAll) * 100).toFixed(1)) : 0,
      ourSchoolFemaleCount: ourFemaleLevel3.toString()
    },
    {
      label: '4수준',
      totalSchool: totalAll > 0 ? parseFloat(((totalLevel4 / totalAll) * 100).toFixed(1)) : 0,
      ourSchool: ourTotalAll > 0 ? parseFloat(((ourLevel4 / ourTotalAll) * 100).toFixed(1)) : 0,
      ourSchoolCount: ourLevel4.toString(),
      totalSchoolMale: totalMaleAll > 0 ? parseFloat(((totalMaleLevel4 / totalMaleAll) * 100).toFixed(1)) : 0,
      totalSchoolFemale: totalFemaleAll > 0 ? parseFloat(((totalFemaleLevel4 / totalFemaleAll) * 100).toFixed(1)) : 0,
      ourSchoolMale: ourMaleAll > 0 ? parseFloat(((ourMaleLevel4 / ourMaleAll) * 100).toFixed(1)) : 0,
      ourSchoolMaleCount: ourMaleLevel4.toString(),
      ourSchoolFemale: ourFemaleAll > 0 ? parseFloat(((ourFemaleLevel4 / ourFemaleAll) * 100).toFixed(1)) : 0,
      ourSchoolFemaleCount: ourFemaleLevel4.toString()
    }
  ];
});

const schoolData = inject('schoolData');

// 영역별 정답률 데이터
const ourClassPercentages = computed(() => {
  if (!currentGradeClasses.value.length) return [0, 0, 0, 0];
  
  const prefix = props.subject === 'literacy' ? 'kor' : 'mat';
  
  // 우리 학교 가중평균 계산 (각 반 인원수 고려)
  let totalStudents = 0;
  let totalCorrect1 = 0, totalCorrect2 = 0, totalCorrect3 = 0, totalCorrect4 = 0;
  
  console.log(`=== ${props.subject} 정답률 계산 시작 ===`);
  console.log('prefix:', prefix);
  
  currentGradeClasses.value.forEach((cls, index) => {
    // 해당 과목 응시자 수 계산 (성별 구분 없이)
    const classStudentCount = props.subject === 'literacy' ? 
      (cls.male_count || 0) + (cls.female_count || 0) :
      (cls.mat_male_count || 0) + (cls.mat_female_count || 0);
    
    console.log(`반 ${index + 1}:`, {
      male_count: cls.male_count || 0,
      female_count: cls.female_count || 0,
      mat_male_count: cls.mat_male_count || 0,
      mat_female_count: cls.mat_female_count || 0,
      classStudentCount,
      sub1rate: cls[`${prefix}sub1rate`] || 0,
      sub2rate: cls[`${prefix}sub2rate`] || 0,
      sub3rate: cls[`${prefix}sub3rate`] || 0,
      sub4rate: cls[`${prefix}sub4rate`] || 0
    });
    
    if (classStudentCount > 0) {
      totalStudents += classStudentCount;
      
      // 정답률 * 인원수 = 해당 반의 총 정답 수
      const correct1 = (cls[`${prefix}sub1rate_origin`] || 0) * classStudentCount / 100;
      const correct2 = (cls[`${prefix}sub2rate_origin`] || 0) * classStudentCount / 100;
      const correct3 = (cls[`${prefix}sub3rate_origin`] || 0) * classStudentCount / 100;
      const correct4 = (cls[`${prefix}sub4rate_origin`] || 0) * classStudentCount / 100;
      
      totalCorrect1 += correct1;
      totalCorrect2 += correct2;
      totalCorrect3 += correct3;
      totalCorrect4 += correct4;
      
    }
  });
  
  // 전체 정답 수 / 전체 인원 = 가중평균 정답률
  const avg1 = totalStudents > 0 ? (totalCorrect1 / totalStudents) * 100 : 0;
  const avg2 = totalStudents > 0 ? (totalCorrect2 / totalStudents) * 100 : 0;
  const avg3 = totalStudents > 0 ? (totalCorrect3 / totalStudents) * 100 : 0;
  const avg4 = totalStudents > 0 ? (totalCorrect4 / totalStudents) * 100 : 0;
  
  return [Math.round(avg1), Math.round(avg2), Math.round(avg3), Math.round(avg4)];
});

const overallPercentages = computed(() => {
  if (!currentGradeData.value) return [0, 0, 0, 0];
  
  const prefix = props.subject === 'literacy' ? 'kor' : 'mat';
  const gradeData = currentGradeData.value;
  
  return [
    gradeData[`${prefix}sub1rate`] || 0,
    gradeData[`${prefix}sub2rate`] || 0,
    gradeData[`${prefix}sub3rate`] || 0,
    gradeData[`${prefix}sub4rate`] || 0
  ];
});

// 그래프 이미지 동적 선택
const graphImage = computed(() => {
  return props.subject === 'literacy' ? literacyGraph02 : numeracyGraph02;
});

// 과목 라벨
const subjectLabel = computed(() => {
  return props.subject === 'literacy' ? '문해력' : '수리력';
});

const pagetype = computed(() => props.subject);
const region = "seoul";
const userRole ="principal";

const props = defineProps({
    schoolType: {
        type: String, 
        required: true,
        validator: (value) => ['elementary', 'middle', 'high'].includes(value),
    },
    gradeCode: {
        type: String,
        default: ''
    },
    subject: {
        type: String,
        default: 'numeracy',
        validator: (value) => ['literacy', 'numeracy'].includes(value)
    }
});

const grade = computed(() => {
    // props로 gradeCode가 넘어오면 우선 사용
    if (props.gradeCode) {
        return props.gradeCode;
    }
    // 기본값
    if (props.schoolType === 'elementary') {
        return '4'; 
    } else if (props.schoolType === 'middle') {
        return '2'; 
    } else if (props.schoolType === 'high') {
        return '1'; 
    }
    return ''; 
});
</script>

<template lang="">
  <div class="slide-inner">
    <div class="content">
      <SubHead :type="pagetype" :region="region" :hideTitleBox="true" />
      <div class="card-wrap">
        <div class="card pt-5">    
          <h4 class="title-box" :class="[region, pagetype, `grade`+grade]">
            <template v-if="props.schoolType === 'elementary'">
              초등학교 {{ grade }}학년
            </template>
            <template v-else-if="props.schoolType === 'middle'">
              중학교 {{ grade }}학년
            </template>
            <template v-else-if="props.schoolType === 'high'">
              고등학교 {{ grade }}학년
            </template>
            <template v-else>
              {{ grade }}학년
            </template>
          </h4>        
          <ExamStatus :pagetype="pagetype" :table-data="examData" />
          <div class="chart-list pt-10" :class="pagetype">
            <div class="chart-wrap total">
                <p>수준별 학생 비율(수)</p>
                <div class="chart">                
                    <div class="legend">
                        <p>{{ subjectLabel }} (단위 : %)</p>
                        <n-flex :wrap="false" :size="10">
                            <p><span></span><span>표집학교</span></p>
                            <p><span></span><span>우리학교</span></p>
                        </n-flex>
                    </div>
                    <ComparisonChart
                    chart-title="수준별 학생 비율(수)"
                    :data="ComparisonData"
                    :chart-type="pagetype"
                    comparison-target="overall"  
                    :max-scale="100"
                    class="pt-[10px]"
                    />
                </div>
            </div>           
            <div class="chart-wrap">
                <p>성별, 수준별 학생 비율(수)</p>
                <div class="chart">        
                    <div class="legend">
                        <p>{{ subjectLabel }} (단위 : %)</p>
                        <n-flex :wrap="false" :size="10">
                            <p><span></span><span>표집학교</span></p>
                            <p><span></span><span>우리학교</span></p>
                        </n-flex>
                    </div>
                    <n-flex :wrap="false" class="pt-[10px]">   
                        <ComparisonChart
                          chart-title="성별, 수준별 학생 비율(수) 남학생"
                          :data="ComparisonData"
                          :chart-type="pagetype"
                          comparison-target="male" 
                          :max-scale="100"
                          chart-height="180px"
                        />       
                        <ComparisonChart
                          chart-title="성별, 수준별 학생 비율(수) 여학생"
                          :data="ComparisonData"
                          :chart-type="pagetype"
                          comparison-target="female" 
                          :max-scale="100"
                          chart-height="180px"
                        />     
                    </n-flex>  
                </div>     
            </div>               
          </div>
          <p class="caution pt-5 pb-5">표집학교: 유층비례 군집표집 방법으로 선정된 학교로서 표집학교 점수는 서울시 해당 학년 모집단의 특성과 분포를 통계적으로 대표하는 지표입니다.</p>                           
        </div>
        <AnswerRates
          :type="pagetype" 
          :graph-src="graphImage"
          :our-class-percentages="ourClassPercentages"
          :overall-percentages="overallPercentages"  
          :user-role="userRole"                   
        />
      </div>      
    </div>
  </div>
</template>